import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import moment from "moment";

import ebiOfficeService from "api/ebiOfficeService";
import Button from "components/Button";
import ConfirmModal from "components/ConfirmModal";
import HollowButton from "components/HollowButton";
import Input from "components/Input";
import Loading from "components/Loading";
import { IEbiOfficeConnection } from "dto/Ebi/IEbiOffice";
import { IError } from "dto/IError";
import { getErrorMessage } from "utils/getErrorMessage";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";
import { FC } from "./typings";

// Połączenie z platformą EBI: portal petenta działa poza urzędem (aplikacja ebi/ na platformie EBI), a Magistrat łączy
// się z jej API — przekazuje pisma wychodzące do adresatów EBI i odbiera złożone wnioski. Adres platformy jest stałą
// konfiguracją serwera Magistratu (Ebi:PlatformUrl), więc bez połączenia formularz ma tylko jednorazowy kod rejestracyjny
// od operatora; po połączeniu stan synchronizacji, „Synchronizuj teraz” (zlecenie dla usługi w tle) i „Rozłącz”.
// Zmienia administrator albo właściciel firmy.

const formatDate = (value?: string | null): string =>
  value ? moment(value).format("DD.MM.YYYY HH:mm") : "";

const EbiConnectionSection: FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, error, isLoading } = ebiOfficeService.useGetConnection();
  const { mutate: connect, isLoading: isConnecting } = ebiOfficeService.useConnect();
  const { mutate: disconnect, isLoading: isDisconnecting } = ebiOfficeService.useDisconnect();
  const { mutate: sync, isLoading: isSyncing } = ebiOfficeService.useSync();

  const [enrollmentCode, setEnrollmentCode] = useState("");
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const connection = data?.data;
  const canEdit = connection?.CanEdit ?? false;
  // komunikat BE (brak firmy, uprawnień) dosłownie; odpowiedź bez komunikatu — serwer bez tej trasy
  const errorText = error ? getErrorMessage(error) || t("ebiOffice.connection.loadError") : null;

  const syncPending = connection?.SyncRequested ?? false;
  const canConnect = canEdit && !!connection?.IsPlatformConfigured && !!enrollmentCode.trim();

  // „Synchronizuj teraz” tylko zleca synchronizację — wynik ogłaszamy, gdy stan połączenia przestanie mieć zlecenie
  // (useGetConnection odświeża go do tego czasu)
  const awaitingSync = useRef(false);
  useEffect(() => {
    if (!connection || connection.SyncRequested || !awaitingSync.current) return;

    awaitingSync.current = false;
    notification({
      type: connection.LastSyncError ? ENotificationType.ERROR : ENotificationType.SUCCESS,
      title: t("ebiOffice.connection.title"),
      text: connection.LastSyncError
        ? t("ebiOffice.connection.lastSyncError", { error: connection.LastSyncError })
        : t("ebiOffice.connection.synced"),
    });
  }, [connection, t]);

  const notifyError = (e: IError) =>
    notification({
      type: ENotificationType.ERROR,
      title: t("ebiOffice.connection.title"),
      text: getErrorMessage(e) || t("errors.somethingWentWrongShort"),
    });

  // stan połączenia zmienia ustawienia EBI (IsConnected, adres portalu) i dostępność kanału „EBI” w kreatorze przesyłki
  const applyConnection = (next: IEbiOfficeConnection) => {
    queryClient.setQueryData(ebiOfficeService.queryKeys.getConnection, { data: next });
    queryClient.invalidateQueries(ebiOfficeService.queryKeys.getSettings);
    queryClient.invalidateQueries("outcommingParcel");
  };

  const handleConnect = () =>
    connect(
      { EnrollmentCode: enrollmentCode.trim() },
      {
        onSuccess: ({ data: next }) => {
          applyConnection(next);
          setEnrollmentCode("");
          notification({
            type: ENotificationType.SUCCESS,
            title: t("ebiOffice.connection.title"),
            text: t("ebiOffice.connection.connectedNotice"),
          });
        },
        onError: notifyError,
      },
    );

  const handleSync = () =>
    sync(undefined, {
      onSuccess: ({ data: next }) => {
        awaitingSync.current = true;
        if (next.SyncRequested) {
          notification({
            type: ENotificationType.INFO,
            title: t("ebiOffice.connection.title"),
            text: t("ebiOffice.connection.syncRequested"),
          });
        }
        applyConnection(next);
      },
      onError: notifyError,
    });

  const handleDisconnect = () => {
    setConfirmDisconnect(false);
    disconnect(undefined, {
      onSuccess: ({ data: next }) => {
        awaitingSync.current = false;
        applyConnection(next);
        // rozłączenie lokalne zawsze się wykonuje; ostrzeżenie BE (dosłownie) — platforma nie odwołała klienta urzędu
        notification({
          type: next.Warning ? ENotificationType.ERROR : ENotificationType.INFO,
          title: t("ebiOffice.connection.title"),
          text: next.Warning || t("ebiOffice.connection.disconnected"),
        });
      },
      onError: notifyError,
    });
  };

  const renderConnected = (current: IEbiOfficeConnection) => (
    <>
      <div className={styles.queue}>
        <div>{t("ebiOffice.connection.connected")}</div>
        {current.ApiUrl && (
          <div className={styles.hint}>{t("ebiOffice.connection.platform", { url: current.ApiUrl })}</div>
        )}
        {current.ConnectedDate && (
          <div className={styles.hint}>
            {t("ebiOffice.connection.connectedDate", { date: formatDate(current.ConnectedDate) })}
          </div>
        )}
        <div className={styles.hint}>
          {current.LastSyncDate
            ? t("ebiOffice.connection.lastSync", { date: formatDate(current.LastSyncDate) })
            : t("ebiOffice.connection.neverSynced")}
        </div>
        {current.SyncRequested && <div className={styles.hint}>{t("ebiOffice.connection.syncPending")}</div>}
        {current.LastSyncError && (
          <div className={styles.error}>
            {t("ebiOffice.connection.lastSyncError", { error: current.LastSyncError })}
          </div>
        )}
      </div>

      {canEdit ? (
        <div className={styles.actions}>
          <HollowButton
            text={t("ebiOffice.connection.disconnect")}
            onClick={() => setConfirmDisconnect(true)}
            disabled={isDisconnecting || isSyncing}
          />
          <Button
            text={t("ebiOffice.connection.sync")}
            onClick={handleSync}
            disabled={isSyncing || syncPending || isDisconnecting}
            loading={isSyncing || syncPending}
          />
        </div>
      ) : (
        <div className={styles.hint}>{t("ebiOffice.connection.readOnly")}</div>
      )}
    </>
  );

  const renderConnectForm = (current: IEbiOfficeConnection) => (
    <>
      <div className={styles.hint}>{t("ebiOffice.connection.notConnected")}</div>

      {!current.IsPlatformConfigured ? (
        <div className={styles.error}>{t("ebiOffice.connection.notConfigured")}</div>
      ) : canEdit ? (
        <>
          {current.ApiUrl && (
            <div className={styles.hint}>{t("ebiOffice.connection.platform", { url: current.ApiUrl })}</div>
          )}
          <div className={styles.hint}>{t("ebiOffice.connection.connectHint")}</div>
          <Input
            value={enrollmentCode}
            onChange={setEnrollmentCode}
            label={t("ebiOffice.connection.enrollmentCode")}
            autoComplete="off"
            onEnter={canConnect && !isConnecting ? handleConnect : undefined}
          />
          <div className={styles.actions}>
            <Button
              text={t("ebiOffice.connection.connect")}
              onClick={handleConnect}
              disabled={!canConnect || isConnecting}
              loading={isConnecting}
            />
          </div>
        </>
      ) : (
        <div className={styles.hint}>{t("ebiOffice.connection.readOnly")}</div>
      )}
    </>
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={styles.loading}>
          <Loading />
        </div>
      );
    }

    if (errorText || !connection) return <div className={styles.error}>{errorText}</div>;

    return connection.IsConnected ? renderConnected(connection) : renderConnectForm(connection);
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{t("ebiOffice.connection.title")}</div>
      <div className={styles.hint}>{t("ebiOffice.connection.description")}</div>

      {renderBody()}

      {confirmDisconnect && (
        <ConfirmModal
          title={t("ebiOffice.connection.disconnectTitle")}
          text={t("ebiOffice.connection.disconnectText")}
          acceptLabel={t("ebiOffice.connection.disconnect")}
          cancelLabel={t("common.cancel")}
          onAccept={handleDisconnect}
          onCancel={() => setConfirmDisconnect(false)}
          showDontShowNextTime={false}
        />
      )}
    </div>
  );
};

export default EbiConnectionSection;
